import { useAuthentication } from '@ecosystem/newron-client-lib';
import { actions } from '@ecosystem/newron-design-system';
import React, { useContext, useEffect } from 'react';

import { NotificationToastType, QualtricsWorkflowIds, UpsellConstants, UpsellName } from '../../../common';
import { useQualtricsAPI } from '../../../hooks/useQualtricsAPI';
import { RetailKADMessageResponse } from '../../../services/Market.service';
import { ClientUserGroup } from '../../../services/Persona.service';
import { UpsellContext, actions as stateActions } from '../../../state';
import { ActionType, setNotificationMessage } from '../../../state/action';
import { handleSurveyButtonClick } from '../../../utils/handleSurveyButtonClick';
import { UpsellMessageType } from '../../targeted-upsell/TargetedUpsell';

type UpsellQualtricsWrapperProps = {
  upsellMessageForSlide?: UpsellMessageType;
  setShowUpsellModal?: (show: boolean) => void;
  setShowRetailUpsellModal?: (show: boolean) => void;
  retailKADUpsellMessage?: RetailKADMessageResponse;
  clientUserGroups: ClientUserGroup;
};

export const UpsellQualtricsWrapper = ({
  upsellMessageForSlide,
  setShowUpsellModal,
  setShowRetailUpsellModal,
  clientUserGroups,
  retailKADUpsellMessage,
}: UpsellQualtricsWrapperProps): JSX.Element => {
  const { user } = useAuthentication();
  const { Button } = actions;
  const { triggerQualtricsWorkflow } = useQualtricsAPI();
  const { dispatch } = useContext(UpsellContext);

  const tellMeMoreHandler = () => {
    dispatch(stateActions.setIsModalOpen(true));
    setShowRetailUpsellModal(false);
  };

  useEffect(() => {
    if (retailKADUpsellMessage) {
      dispatch({
        type: ActionType.SetRetailKADUpsellMessage,
        payload: retailKADUpsellMessage,
      });
    }
  }, [dispatch, retailKADUpsellMessage]);

  const onSurveyButtonClick = async (response: boolean) => {
    try {
      await handleSurveyButtonClick({
        response,
        user,
        upsellContent: upsellMessageForSlide?.upsellMessage,
        market: upsellMessageForSlide?.countryCode + '-' + upsellMessageForSlide?.itemGroupCode,
        userGroupIds: clientUserGroups?.user_groups.map(group => group.user_group_id).join(','),
        userGroupNames: clientUserGroups?.user_groups.map(group => group.user_group_name).join(','),
        country: user?.country,
        clientName: clientUserGroups?.client_name,
        clientId: clientUserGroups?.client_id,
        urlTokenId: QualtricsWorkflowIds.TARGETED_UPSELL,
        upsellType: UpsellName.MONTHLY_INSIGHTS,
        dataPeriod: upsellMessageForSlide?.dataPeriodDisplay,
        upsellName: UpsellName.MONTHLY_INSIGHTS,
        triggerQualtricsWorkflow,
      });
      dispatch(
        setNotificationMessage({
          message: UpsellConstants.ToastMessageSuccess,
          type: NotificationToastType.Success,
          timeout: 3000,
        })
      );
    } catch (error) {
      dispatch(
        setNotificationMessage({
          message: UpsellConstants.ToastMessageError,
          type: NotificationToastType.Error,
          timeout: 3000,
        })
      );
    } finally {
      setShowUpsellModal(false);
    }
  };

  return upsellMessageForSlide?.upsellMessage ? (
    <div>
      <Button testId="qualtrics-negative" mode="secondary" onClick={() => onSurveyButtonClick(false)}>
        No thanks
      </Button>
      <Button testId="qualtrics-positive" onClick={() => onSurveyButtonClick(true)}>
        Yes I'm interested
      </Button>
    </div>
  ) : (
    <>
      <Button testId="qualtrics-positive-popup" onClick={tellMeMoreHandler}>
        Tell me more
      </Button>
    </>
  );
};
